import React, { useState, useContext, useEffect } from "react";

import Button from "@material-ui/core/Button";
import axios from 'axios';

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionActions from "@material-ui/core/AccordionActions";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";


// Icons
import AccountCircle from "@material-ui/icons/AccountCircle";
import DeleteIcon from "@material-ui/icons/Delete";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import PhonelinkEraseIcon from "@material-ui/icons/PhonelinkErase";
import EditIcon from '@material-ui/icons/Edit';

import { UserContext } from "../../contexts/UserContext";

import InfoTooltip from "../general/InfoTooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 1000,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  icon: {
    verticalAlign: "bottom",
    height: 20,
    width: 20,
    marginRight: theme.spacing(2),
    display: "inline-block",
  },
  details: {
    alignItems: "center",
  },
  buttonIconLeft: {
    marginRight: theme.spacing(1),
  },
  selectField: {
    width: "85%",
  },
  inactiveRow: {
    opacity: 0.55,
  },
  inactiveHint: {
    marginLeft: theme.spacing(1),
  },
}));

const UsersList = (props) => {
  const theme = useTheme();
  const classes = useStyles();

  const userContext = useContext(UserContext);
  const isBackoffice = userContext.user.role === "backoffice";


  const [expandedUserID, setExpandedUserID] = useState(false);
  const [editingUser, setEditingUser] = useState(false);

  const handleUserSwitch = (id) => (event, isExpanded) => {
    setExpandedUserID(isExpanded ? id : false);
    props.users.some((user) => {
      if (user.id === id) {
        setEditingUser({
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
          active: user.active,
          case_count: user.case_count || 0,
          mfa_status: user.mfa_status || "not_configured",
          new_password: "",
          changePassword: false
        });
        return true;
      }
      return false;
    });
  };

  const handleUserFieldChange = (property) => (event) => {
    if (!editingUser || !editingUser.id) {
      return;
    }
    if (isBackoffice && editingUser.role === "administrator") {
      return;
    }
    setEditingUser({ ...editingUser, [property]: event.target.value });
  };


  const renderUser = (user) => {
    const canManageActivation = !isBackoffice || ["agent", "tippgeber"].includes(user.role);
    const hasAssignedCredits = (user.case_count || 0) > 0;
    const canDeleteUser = canManageActivation && !hasAssignedCredits;
    const isAdminTargetForBackoffice = isBackoffice && user.role === "administrator";
    const isInactive = user.active === false;
    const canResetMfa = user.mfa_status === "active"
      && user.id !== userContext.user.ID
      && (!isBackoffice || user.role !== "administrator");

    return <Accordion
      key={user.id}
      expanded={expandedUserID === user.id}
      onChange={handleUserSwitch(user.id)}
      className={isInactive ? classes.inactiveRow : ""}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Typography className={classes.heading}>
              <AccountCircle className={classes.icon} color="primary" />
              {user.name}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography className={classes.secondaryHeading}>
              {user.email}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="overline">
              {user.role}
              {isInactive && (
                <span className={classes.inactiveHint}>inaktives Profil</span>
              )}
            </Typography>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <Grid container spacing={3}>
          <Grid item xs={2}>
            <TextField
              label="Name"
              className={classes.textField}
              value={editingUser.name ? editingUser.name : ""}
              onChange={handleUserFieldChange("name")}
              margin="none"
              disabled={isAdminTargetForBackoffice}
            />
          </Grid>
          {editingUser.id !== userContext.user.ID && (
            <React.Fragment>
              <Grid item xs={3}>
                <TextField
                  name="role"
                  id="role"
                  label="Benutzerrolle"
                  className={classes.selectField}
                  value={editingUser.role ? editingUser.role : "tippgeber"}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleUserFieldChange("role")}
                  margin="none"
                  fullWidth
                  select
                  disabled={isAdminTargetForBackoffice}
                >
                  {isBackoffice && editingUser.role === "administrator" && (
                    <MenuItem value="administrator" disabled>
                      Administrator (nicht zuweisbar)
                    </MenuItem>
                  )}
                  <MenuItem value="tippgeber">Tippgeber</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                  <MenuItem value="backoffice">Backoffice</MenuItem>
                  {!isBackoffice && <MenuItem value="administrator">Administrator</MenuItem>}
                </TextField>
                <InfoTooltip
                  content={
                    <React.Fragment>
                      Administratoren können:
                      <ul>
                        <li>Benutzer anlegen</li>
                        <li>andere Benutzer bearbeiten oder löschen</li>
                        <li>allgemeine Einstellungen ändern</li>
                      </ul>
                    </React.Fragment>
                  }
                />
              </Grid>
              <Grid item xs={3}>
                {editingUser.changePassword ?
                  <TextField
                    id={`new-password-${user.id}`}
                    label="Neues Passwort"
                    type="password"
                    autoComplete="new-password"
                    className={classes.textField}
                    value={editingUser.new_password ? editingUser.new_password : ""}
                    onChange={handleUserFieldChange("new_password")}
                    helperText="mindestens 10 Zeichen"
                    margin="none"
                    disabled={isAdminTargetForBackoffice}
                  />
                  :
                  <>
                    <Button size="small" disabled={isAdminTargetForBackoffice} onClick={() => {
                      setEditingUser({ ...editingUser, "changePassword": true });
                    }}>
                      <VpnKeyIcon className={classes.buttonIconLeft} />
                      Passwort zurücksetzen
                    </Button>
                    <InfoTooltip
                      content={
                        <React.Fragment>
                          Das bestehende Passwort kann nicht ausgelesen werden, du kannst es nur mit einem neuen überschreiben
                        </React.Fragment>
                      }
                    />
                  </>
                }
              </Grid>
              <Grid item xs={3}>
                {editingUser.changeEmail ?
                  <TextField
                    label="E-Mail ändern"
                    className={classes.textField}
                    value={editingUser.email}
                    onChange={handleUserFieldChange("email")}
                    helperText="mindestens 10 Zeichen"
                    margin="none"
                    disabled={isAdminTargetForBackoffice}
                  />
                  :
                  <>
                    <Button size="small" disabled={isAdminTargetForBackoffice} onClick={() => {
                      setEditingUser({ ...editingUser, "changeEmail": true });
                    }}>
                      <EditIcon className={classes.buttonIconLeft} />
                      E-Mail ändern
                    </Button>
                    <InfoTooltip
                      content={
                        <React.Fragment>
                          Ändere die E-Mail Adresse des Benutzers.
                        </React.Fragment>
                      }
                    />
                  </>
                }
              </Grid>
              <Grid item xs={3}>
                {canManageActivation && (
                  <>
                    {canDeleteUser ? (
                      <>
                        <Button size="small" onClick={() => props.deleteUser(editingUser)}>
                          <DeleteIcon className={classes.buttonIconLeft} />
                          Benutzer löschen
                        </Button>
                        <InfoTooltip
                          content={
                            <React.Fragment>
                              Achtung, es gibt hier kein zurück. Ein gelöschter
                              Benutzer bleibt für immer gelöscht.
                            </React.Fragment>
                          }
                        />
                      </>
                    ) : (
                      <>
                        <Button
                          size="small"
                          onClick={() => {
                            const nextActive = !editingUser.active;
                            const nextEditingUser = {
                              ...editingUser,
                              active: nextActive,
                            };
                            setEditingUser(nextEditingUser);
                            props.toggleUserActive(nextEditingUser, nextActive);
                          }}
                          disabled={isAdminTargetForBackoffice}
                        >
                          <DeleteIcon className={classes.buttonIconLeft} />
                          {editingUser.active ? "Benutzer deaktivieren" : "Benutzer aktivieren"}
                        </Button>
                        <InfoTooltip
                          content={
                            <React.Fragment>
                              Dieser Benutzer hat zugeordnete Finanzierungen und kann daher nicht gelöscht werden.
                            </React.Fragment>
                          }
                        />
                      </>
                    )}
                  </>
                )}
              </Grid>
            </React.Fragment>
          )}
        </Grid>
      </AccordionDetails>
      <Divider />
      <AccordionActions>
		{canResetMfa && <Button
			size="small"
			color="secondary"
			onClick={() => props.resetUserMfa(user)}
		>
			<PhonelinkEraseIcon className={classes.buttonIconLeft} />
			2FA zurücksetzen
		</Button>}
		<Typography variant="caption">
			2FA: {user.mfa_status === "active" ? "eingerichtet" : "nicht eingerichtet"}
		</Typography>
        <Button size="small" onClick={handleUserSwitch(null)}>
          Abbrechen
        </Button>
        <Button
          size="small"
          color="primary"
          disabled={
            !editingUser
            || !editingUser.id
            || isAdminTargetForBackoffice
            || (isBackoffice && editingUser.role === "backoffice")
            || (editingUser.changePassword && editingUser.new_password.length < 10)
          }
          onClick={() => props.saveUser(editingUser, () => setEditingUser({
            ...editingUser,
            changePassword: false,
            new_password: ""
          }))}>
          Speichern
        </Button>
      </AccordionActions>
    </Accordion>;
  }
  return (
    <div>
      {
        props.users.map((user) => {
          return renderUser(user);
        })
      }
    </div>
  )
};

export default UsersList;
